import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPersonas, askPersona, getMyThreads, saveThread } from '../services/api';
import toast from 'react-hot-toast';

interface Persona { id: string; name: string; title: string; specialty: string; icon: string; description: string; }
interface Message { role: 'user' | 'assistant'; content: string; }
interface SavedThread { thread_id: string; persona_id: string; persona_name: string; title: string; message_count: number; created_at: string; }

export default function CommunityPage() {
  const { currentUser } = useAuth();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selected, setSelected] = useState<Persona | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPersonas, setLoadingPersonas] = useState(true);
  const [savedThreads, setSavedThreads] = useState<SavedThread[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const [personaRes, threadRes] = await Promise.allSettled([
        getPersonas(token),
        getMyThreads(token),
      ]);
      if (personaRes.status === 'fulfilled') setPersonas(personaRes.value.personas || []);
      if (threadRes.status === 'fulfilled') setSavedThreads(threadRes.value.threads || []);
    } catch { toast.error('Failed to load AI lawyers'); }
    finally { setLoadingPersonas(false); }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const selectPersona = (p: Persona) => {
    setSelected(p);
    setShowHistory(false);
    setMessages([{
      role: 'assistant',
      content: `Hello, I'm ${p.name}, a ${p.title}. ${p.description} How can I help you today?\n\n*I provide general legal information only — for advice on your specific situation, please consult a qualified solicitor.*`,
    }]);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !selected || loading || !currentUser) return;
    const q = question;
    const updatedMessages: Message[] = [...messages, { role: 'user', content: q }];
    setMessages(updatedMessages);
    setQuestion('');
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      // Pass conversation history (skip the initial greeting) for multi-turn context
      const history = updatedMessages.slice(1).map((m) => ({ role: m.role, content: m.content }));
      const res = await askPersona(token, selected.id, q, history);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.response }]);
    } catch (e: any) {
      toast.error(e.message || 'Failed');
      setMessages((prev) => prev.slice(0, -1));
      setQuestion(q);
    } finally { setLoading(false); }
  };

  const handleSaveThread = async () => {
    if (!currentUser || !selected || messages.length < 3) return; // need at least greeting + 1 exchange
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      await saveThread(token, selected.id, selected.name, messages);
      toast.success('Conversation saved!');
      // Refresh saved threads
      const res = await getMyThreads(token);
      setSavedThreads(res.threads || []);
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // Count user messages (actual exchanges, not the greeting)
  const userMessageCount = messages.filter((m) => m.role === 'user').length;

  if (loadingPersonas) return <div className="flex items-center justify-center h-64 text-gray-400">Loading AI lawyers...</div>;

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">AI Legal Specialists</h1>
        <p className="text-gray-500 text-sm mt-1">General legal information — no personal documents shared in community chat.</p>
      </div>
      <div className="flex gap-6 h-[calc(100vh-220px)]">
        {/* Sidebar: Personas + Saved conversations */}
        <div className="w-56 flex-shrink-0 flex flex-col overflow-hidden">
          {/* Tab toggle */}
          <div className="flex mb-2 bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setShowHistory(false)}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${!showHistory ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}>
              Specialists
            </button>
            <button onClick={() => setShowHistory(true)}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${showHistory ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}>
              History {savedThreads.length > 0 && <span className="text-gray-400">({savedThreads.length})</span>}
            </button>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto">
            {!showHistory ? (
              // Persona list
              personas.map((p) => (
                <button key={p.id} onClick={() => selectPersona(p)}
                  className={`w-full text-left px-3 py-3 rounded-xl border transition-colors ${
                    selected?.id === p.id && !showHistory ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'
                  }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{p.icon}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">{p.title}</p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              // Saved conversations
              savedThreads.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-xs">
                  <p>No saved conversations yet</p>
                  <p className="mt-1">Chat with a specialist and save the conversation</p>
                </div>
              ) : (
                savedThreads.map((t) => (
                  <div key={t.thread_id}
                    className="w-full text-left px-3 py-3 rounded-xl border border-gray-200 bg-white hover:border-blue-200 transition-colors">
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2">{t.title}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t.persona_name} · {t.message_count} messages · {t.created_at?.slice(0, 10)}
                    </p>
                  </div>
                ))
              )
            )}
          </div>
        </div>

        {/* Chat */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><div className="text-4xl mb-3">👨‍⚖️</div><p>Select an AI lawyer to begin</p><p className="text-xs mt-1 text-gray-300">General information only</p></div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-gray-100 bg-slate-50 flex items-center gap-3">
                <span className="text-2xl">{selected.icon}</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{selected.name}</p>
                  <p className="text-xs text-gray-500">{selected.title}</p>
                </div>
                <span className="ml-auto text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">General info only</span>
                {userMessageCount >= 1 && (
                  <button onClick={handleSaveThread} disabled={saving}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 transition-colors">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-sm mr-2 flex-shrink-0 mt-0.5">{selected.icon}</div>
                    )}
                    <div className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user' ? 'bg-blue-700 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-sm mr-2">{selected.icon}</div>
                    <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                      <div className="flex gap-1">
                        {[0, 150, 300].map((d) => <div key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={handleSend} className="px-4 py-4 border-t border-gray-100 flex gap-2">
                <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
                  placeholder={`Ask ${selected.name} a legal question...`} disabled={loading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button type="submit" disabled={loading || !question.trim()}
                  className="px-5 py-2.5 bg-blue-700 text-white rounded-xl text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors">
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
